import { useState, useEffect, useMemo, useCallback } from 'react'
import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa'
import { useSelector } from 'react-redux'

import PageHeader from '../../../../shared/PageHeader'
import ReportFilters from '../../../../shared/ReportFilters'
import ReportJobsList from '../../../../shared/ReportJobsList'
import useAsyncReport from '../../../../hooks/useAsyncReport'
import useFetchData from '../../../../hooks/UseFetchData'
import { payersOptionsFormat } from '../../../../constants/BeneficiaryConstants'
import { fetchData } from '../../../../helpers/fetchData'
import {
  addInsuranceReportJob,
  updateInsuranceReportJob,
  removeInsuranceReportJob
} from '../../../../redux/slices/AppSlice'
import { validateOneMonthRange, getOneMonthMaxDate, getMinDate } from '../../../../utils/dateRangeValidation'

const PayersSummaryReportAsync = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const jobs = useSelector(state => state.app.insuranceReportJobs || [])
  const loggedUser = useSelector(state => state.auth.user)
  const shouldSkipItemCategory = Number(loggedUser?.business_id) == 27

  const { filters, setFilters, resetFilters } = usePersistedFilters(
    'payers_summary_async_new',
    {
      start_date: moment().subtract(1, 'day').toDate(),
    end_date: moment().toDate(),
    payer_id: '',
    department_id: '',
    policy_numbers: [],
    invoice_number: '',
    file_number: '',
    category: 'insurance',
    format: 'pdf'
    }
  )

  const [payers, setPayers] = useState([])
  const [departments, setDepartments] = useState([])
  const [policies, setPolicies] = useState([])

  const { executeFetch: fetchPayers } = useFetchData()
  const { executeFetch: fetchPolicies } = useFetchData()

  const { loading, downloadingJobs, startJob, downloadReport, removeJob, retryJob } = useAsyncReport({
    addJobAction: addInsuranceReportJob,
    updateJobAction: updateInsuranceReportJob,
    removeJobAction: removeInsuranceReportJob,
    createEndpoint: '/v3/reports/insurance/jobs',
    statusEndpoint: '/v3/reports/insurance/jobs',
    downloadEndpoint: '/v3/reports/insurance/jobs',
    fileNamePrefix: 'insurance_report',
    existingJobs: jobs,
    buildPayload: (filters, format) => ({
      start_date: moment(filters.start_date).format('YYYY-MM-DD'),
      end_date: moment(filters.end_date).format('YYYY-MM-DD'),
      payer_id: filters.payer_id || undefined,
      department_id: filters.department_id || undefined,
      policy_numbers: filters.policy_numbers.length > 0 ? filters.policy_numbers : undefined,
      invoice_number: filters.invoice_number || undefined,
      file_number: filters.file_number || undefined,
      ...(shouldSkipItemCategory
        ? { skip_item_category: true }
        : { item_category: filters.category || "insurance" }),
      format
    })
  })

  const loadDepartments = useCallback(async () => {
    try {
      const res = await fetchData('POST', '/v3/department/getList', {})
      if (res?.data) {
        setDepartments(res.data.map(d => ({ label: d.name_en, value: d.id })))
      }
    } catch (error) {
      console.log(error)
    }
  }, [])

  const loadPayers = useCallback(async () => {
    const res = await fetchPayers('POST', '/v3/payer/get', {})
    if (res?.data) {
      setPayers(res?.data)
    }
  }, [fetchPayers])

  const loadPolicies = useCallback(async () => {
    if (filters.payer_id) {
      const res = await fetchPolicies('POST', '/v3/insurancePolicy/getByPayerId', {
        payer_id: filters.payer_id
      })
      if (res?.data) {
        setPolicies(res.data.map(p => ({ label: p.policy_number, value: p.policy_number })))
      }
    } else {
      setPolicies([])
      setFilters(prev => ({ ...prev, policy_numbers: [] }))
    }
  }, [filters.payer_id, fetchPolicies])

  useEffect(() => {
    loadDepartments()
  }, [loadDepartments])

  useEffect(() => {
    loadPayers()
  }, [loadPayers])

  useEffect(() => {
    loadPolicies()
  }, [loadPolicies])

  const handleGenerate = async () => {
    if (!validateOneMonthRange(filters.start_date, filters.end_date, t)) return
    await startJob(filters, filters.format)
  }

  const handleReset = () => {
    resetFilters()
  }

    const handleChange = e => {
    const { name, value } = e.target
    
    if (name === 'payer_id' && value) {
      const selectedPayer = payers.find(payer => payer.payer_id === value)
      const payerCategory = selectedPayer?.category || 'insurance'
      
      setFilters(prev => ({ 
        ...prev, 
        [name]: value,
        category: payerCategory
      }))
    } else if (name === 'payer_id' && !value) {
      setFilters(prev => ({ 
        ...prev, 
        [name]: value,
        category: 'insurance'
      }))
    } else {
      setFilters(prev => ({ ...prev, [name]: value }))
    }
  }

  const filterConfig = useMemo(() => [
    {
      type: 'date',
      name: 'start_date',
      label: 'Start Date',
      width: 'w-[160px]'
    },
    {
      type: 'date',
      name: 'end_date',
      label: 'End Date',
      width: 'w-[160px]',
      minDate: getMinDate(filters.start_date),
      maxDate: getOneMonthMaxDate(filters.start_date)
    },
    {
      type: 'dropdown',
      name: 'payer_id',
      label: 'Payer',
      options: payersOptionsFormat(payers),
      placeholder: 'All Payers',
      filter: true,
      width: 'w-[200px]'
    },
    {
      type: 'dropdown',
      name: 'department_id',
      label: 'Department',
      options: departments,
      placeholder: 'All Departments',
      filter: true,
      width: 'w-[200px]'
    },
    {
      type: 'dropdown',
      name: 'policy_numbers',
      label: 'Policy Numbers',
      options: policies,
      placeholder: 'All Policies',
      filter: true,
      multiple: true,
      disabled: !filters.payer_id,
      width: 'w-[200px]'
    },
    {
      type: 'text',
      name: 'invoice_number',
      label: 'Invoice Number',
      placeholder: 'Invoice Number',
      width: 'w-[160px]'
    },
    {
      type: 'text',
      name: 'file_number',
      label: 'MRN',
      placeholder: 'MRN',
      width: 'w-[160px]'
    },
    ...(!shouldSkipItemCategory ? [{
      type: 'dropdown',
      name: 'category',
      label: 'Category',
      options: [
        // { label: t('All'), value: '' },
        { label: t('Insurance'), value: 'insurance' },
        { label: t('Corporate'), value: 'corporate' },
        { label: t('Government'), value: 'government' }
      ],
      placeholder: 'Insurance',
      width: 'w-[160px]'
    }] : []),
    {
      type: 'dropdown',
      name: 'format',
      label: 'Format',
      options: [
        { label: 'Excel', value: 'excel' },
        { label: 'PDF', value: 'pdf' }
      ],
      width: 'w-[160px]'
    }
  ], [payers, departments, policies, filters.payer_id, shouldSkipItemCategory, t, filters.start_date])

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <div className='flex items-center justify-between'>
        <PageHeader
          title={t('Payers Summary Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <ReportFilters
        filters={filters}
        onChange={handleChange}
        filterConfig={filterConfig}
        onGenerate={handleGenerate}
        onReset={handleReset}
        loading={loading}
      />

      <ReportJobsList
        jobs={jobs}
        onDownload={downloadReport}
        onRemove={removeJob}
        onRetry={retryJob}
        downloadingJobs={downloadingJobs}
        reportTitle={t('Payers Summary Report')}
      />
    </div>
  )
}

export default PayersSummaryReportAsync
